import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
    fetchCatalogComplianceItemsAdded,
    fetchCatalogComplianceItemsRecategorized,
    fetchCatalogComplianceItemsRemoved,
    fetchCatalogComplianceItemsUncategorized,
    fetchCatalogComplianceMarketplacePurchases,
    fetchCatalogComplianceSummary,
} from '../services/catalogComplianceServices';

import type {
    CatalogComplianceLastRequestParams,
    CatalogComplianceResourceState,
    ReportCatalogComplianceSchema,
} from '../types/ReportCatalogComplianceSchema';

const createResourceState = <Data>(): CatalogComplianceResourceState<Data> => ({
    data: null,
    loading: false,
    error: null,
    requestId: null,
});

const applyResourcePending = <Data>(
    resource: CatalogComplianceResourceState<Data>,
    requestId: string,
): void => {
    resource.loading = true;
    resource.error = null;
    resource.requestId = requestId;
};

const applyResourceFulfilled = <Data>(
    resource: CatalogComplianceResourceState<Data>,
    requestId: string,
    data: Data,
): void => {
    if (resource.requestId !== requestId) return;
    resource.data = data;
    resource.loading = false;
    resource.error = null;
};

const applyResourceRejected = <Data>(
    resource: CatalogComplianceResourceState<Data>,
    requestId: string,
    error: string,
): void => {
    if (resource.requestId !== requestId) return;
    resource.loading = false;
    resource.error = error;
};

const initialState: ReportCatalogComplianceSchema = {
    summary: createResourceState(),
    marketplacePurchases: createResourceState(),
    itemsAdded: createResourceState(),
    itemsRemoved: createResourceState(),
    itemsRecategorized: createResourceState(),
    itemsUncategorized: createResourceState(),
    lastRequestParams: null,
};

export const reportCatalogComplianceSlice = createSlice({
    name: 'reportCatalogCompliance',
    initialState,
    reducers: {
        setCatalogComplianceLastRequestParams: (
            state,
            action: PayloadAction<CatalogComplianceLastRequestParams>,
        ) => {
            state.lastRequestParams = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCatalogComplianceSummary.pending, (state, action) => {
                applyResourcePending(state.summary, action.meta.requestId);
            })
            .addCase(
                fetchCatalogComplianceSummary.fulfilled,
                (state, action) => {
                    applyResourceFulfilled(
                        state.summary,
                        action.meta.requestId,
                        action.payload,
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceSummary.rejected,
                (state, action) => {
                    applyResourceRejected(
                        state.summary,
                        action.meta.requestId,
                        action.payload ?? 'error',
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceMarketplacePurchases.pending,
                (state, action) => {
                    applyResourcePending(
                        state.marketplacePurchases,
                        action.meta.requestId,
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceMarketplacePurchases.fulfilled,
                (state, action) => {
                    applyResourceFulfilled(
                        state.marketplacePurchases,
                        action.meta.requestId,
                        action.payload,
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceMarketplacePurchases.rejected,
                (state, action) => {
                    applyResourceRejected(
                        state.marketplacePurchases,
                        action.meta.requestId,
                        action.payload ?? 'error',
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsAdded.pending,
                (state, action) => {
                    applyResourcePending(
                        state.itemsAdded,
                        action.meta.requestId,
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsAdded.fulfilled,
                (state, action) => {
                    applyResourceFulfilled(
                        state.itemsAdded,
                        action.meta.requestId,
                        action.payload,
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsAdded.rejected,
                (state, action) => {
                    applyResourceRejected(
                        state.itemsAdded,
                        action.meta.requestId,
                        action.payload ?? 'error',
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsRemoved.pending,
                (state, action) => {
                    applyResourcePending(
                        state.itemsRemoved,
                        action.meta.requestId,
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsRemoved.fulfilled,
                (state, action) => {
                    applyResourceFulfilled(
                        state.itemsRemoved,
                        action.meta.requestId,
                        action.payload,
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsRemoved.rejected,
                (state, action) => {
                    applyResourceRejected(
                        state.itemsRemoved,
                        action.meta.requestId,
                        action.payload ?? 'error',
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsRecategorized.pending,
                (state, action) => {
                    applyResourcePending(
                        state.itemsRecategorized,
                        action.meta.requestId,
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsRecategorized.fulfilled,
                (state, action) => {
                    applyResourceFulfilled(
                        state.itemsRecategorized,
                        action.meta.requestId,
                        action.payload,
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsRecategorized.rejected,
                (state, action) => {
                    applyResourceRejected(
                        state.itemsRecategorized,
                        action.meta.requestId,
                        action.payload ?? 'error',
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsUncategorized.pending,
                (state, action) => {
                    applyResourcePending(
                        state.itemsUncategorized,
                        action.meta.requestId,
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsUncategorized.fulfilled,
                (state, action) => {
                    applyResourceFulfilled(
                        state.itemsUncategorized,
                        action.meta.requestId,
                        action.payload,
                    );
                },
            )
            .addCase(
                fetchCatalogComplianceItemsUncategorized.rejected,
                (state, action) => {
                    applyResourceRejected(
                        state.itemsUncategorized,
                        action.meta.requestId,
                        action.payload ?? 'error',
                    );
                },
            );
    },
});

export const { reducer: reportCatalogComplianceReducer } =
    reportCatalogComplianceSlice;

export const { setCatalogComplianceLastRequestParams } =
    reportCatalogComplianceSlice.actions;
